import {
  DataGridPro,
  getGridDateOperators,
  getGridNumericOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GRID_CHECKBOX_SELECTION_FIELD,
  GridActionsCellItem,
  GridColDef,
  GridFilterInputDate,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridRowSelectionModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import {
  GridFilterItem,
  GridFilterModel,
  GridLogicOperator,
  GridSortModel,
  GridToolbarProps,
} from "@mui/x-data-grid";
import {
  currencyFormatter,
  InvoiceListItem,
  SavedFilterType,
  statusUIMapXero,
  toDecimal,
  getAccountingSystemDeepLink,
} from "shared";
import { Tooltip } from "@mui/material";
import {
  CalendarDateRangeIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  HandRaisedIcon,
  LockClosedIcon,
  LockOpenIcon,
} from "@heroicons/react/20/solid";
import {
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocalStorage } from "usehooks-ts";
import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

import {
  InvoiceTableFields,
  useInvoices,
  useInvoiceTableModels,
} from "../../hooks/useInvoices";
import { usePersistedTableModels } from "../../hooks/usePersistedTableModels";
import { classNames } from "../../../common/lib/classNames";
import { WrappedLink } from "../WrappedLink";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { Button } from "../../../common/Atoms/Button";
import { XeroStatusBadge } from "../../../common/Components/Invoice/XeroStatusBadge";
import { useGroups } from "../../hooks/useGroups";
import { useInvoiceTags } from "../../hooks/useInvoiceTags";
import { TagBadge } from "../Molecules/TagBadge";
import { NotesCellButton } from "../Molecules/NotesDrawer";
import { Heading } from "../../../common/Atoms/Typography/Heading";
import { useAccountingSystemName } from "../../hooks/useAccountingSystemName";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import {
  useActionInvoiceMutation,
  useActionInvoicesMutation,
  useExportAllInvoicesMutation,
} from "../../services/api/invoiceApi/invoice";
import { buildInvoiceListParamsFromFilterModel } from "../../lib/invoiceFilterParams";
import { env } from "../../../common/lib/env";
import {
  commonConfig,
  filterColumns,
  getColumnForNewFilter,
  getTogglableColumns,
  onRowSelectionChangeWrapper,
  toSavedFilterExportParams,
} from "../../lib/datagrid";
import { CustomGenericToolbar } from "../Common/Datagrid/CustomGenericToolbar";
import { ApiSavedFilterPanel } from "../Common/Datagrid/ApiSavedFilterPanel";
import { SaveFilterButton } from "../Common/Datagrid/SaveFilterButton";
import { DatagridCard } from "../Common/Datagrid/DatagridCard";
import { useApplySavedFilterFromUrl } from "../../hooks/useApplySavedFilterFromUrl";
import { buildInvoiceAgingBucketFilterItems, InvoiceAgingBucket } from "../../lib/invoiceAgingBuckets";

import { InvoiceBulkActionsToolbar } from "./InvoiceBulkActionsToolbar";
import { InvoiceKanbanBoard } from "./InvoiceKanbanBoard";
import { InvoiceViewToggle } from "./InvoiceViewToggle";

// eslint-disable-next-line quotes
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    onRemoveFilter: (filterId: GridFilterItem[`id`]) => void;
    rowSelectionModel: GridRowSelectionModel;
    context: `default` | `contact`;
  }
}

type ToolbarProps = GridToolbarProps & {
  onRemoveFilter: (filterId: GridFilterItem[`id`]) => void;
  rowSelectionModel: GridRowSelectionModel;
  context: `default` | `contact`;
};

const statusOptions = Object.keys(statusUIMapXero).map(key => {
  return {
    ...statusUIMapXero[key],
    label: statusUIMapXero[key].name,
    value: key,
  };
});

const freezeOptions = [
  {
    label: `Frozen`,
    value: true,
  },
  {
    label: `Not Frozen`,
    value: false,
  },
];

const isLateFeeOptions = [
  {
    label: `Only Late Fees`,
    value: true,
  },
  {
    label: `Late Fees Excluded`,
    value: false,
  },
];

const hasPaymentPlanOptions = [
  {
    label: `Has Payment Plan`,
    value: true,
  },
  {
    label: `No Payment Plan`,
    value: false,
  },
];

const hasOpenQueryOptions = [
  {
    label: `Has Open Query`,
    value: true,
  },
  {
    label: `No Open Query`,
    value: false,
  },
];

const hasPaymentPromiseOptions = [
  {
    label: `Has Payment Promise`,
    value: true,
  },
  {
    label: `No Payment Promise`,
    value: false,
  },
];

// ── Stacked columns ───────────────────────────────────────────────────────
// Each entry renders the secondary column's value as a smaller second line
// inside the primary column's cell. The secondary column stays defined on the
// grid (hidden by default) so it can still be shown, sorted and filtered on
// its own. To stack another column: add an entry here and hide the secondary
// field in defaultColumnVisibilityModel.
const stackedColumns = [
  {
    field: InvoiceTableFields.invoiceNumber,
    secondaryField: InvoiceTableFields.invoiceReference,
    getSecondaryValue: (row: InvoiceListItem) => row.invoice.reference,
  },
  {
    field: InvoiceTableFields.contactName,
    secondaryField: InvoiceTableFields.primaryContactName,
    getSecondaryValue: (row: InvoiceListItem) => row.contact.primaryContactName,
  },
];

interface InvoiceTableRootProps {
  context?: `default` | `contact`;
}

// Same params the table query uses, minus paging — used for the emailed export and saved filter schedules
function buildInvoiceExportParams(filterModel: GridFilterModel, sortModel: GridSortModel, contactIdParam?: string) {
  const params = buildInvoiceListParamsFromFilterModel(filterModel, sortModel);

  delete (params as Partial<typeof params>).page;
  delete params.limit;

  if (contactIdParam) {
    // When contactID is present in URL, we must be on the contact page, so override it
    params.contactId = Number(contactIdParam);
    params.groupId = undefined;
  }

  return params;
}

export function InvoiceTableRoot({
  context = `default`,
}: InvoiceTableRootProps) {
  const [persistedInvoiceListView, setPersistedInvoiceListView] = useLocalStorage<`table` | `board`>(`invoiceListView`, `table`);
  const [contactInvoiceListView, setContactInvoiceListView] = useState<`table` | `board`>(`table`);
  const { contactId } = useParams();
  const invoiceListView = context === `contact` ? contactInvoiceListView : persistedInvoiceListView;
  const setInvoiceListView = context === `contact` ? setContactInvoiceListView : setPersistedInvoiceListView;
  const showKanbanBoard = invoiceListView === `board`;
  const {
    data: invoicesRes,
    currentData: currentInvoicesRes,
    isLoading,
    isFetching,
  } = useInvoices({ skip: showKanbanBoard });
  const { data: groupsRes } = useGroups();
  const { tags: invoiceTagsList, colorMap: invoiceTagColorMap } = useInvoiceTags();
  const currentOrg = useGetSelectedOrganisation();
  const accountingSystemName = useAccountingSystemName();
  const [ actionInvoices] = useActionInvoicesMutation();
  const [actionInvoice] = useActionInvoiceMutation();
  const apiRef = useGridApiRef();
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const defaultColumnVisibilityModel = useMemo(() => ({
    [InvoiceTableFields.invoiceSentStatus]: false,
    [InvoiceTableFields.expectedPaymentDate]: false,
    [InvoiceTableFields.amountLastChangedDate]: false,
    [InvoiceTableFields.currency]: false,
    // Stacked into the Number / Contact cells
    [InvoiceTableFields.invoiceReference]: false,
    [InvoiceTableFields.primaryContactName]: false,
    // Rendered as badge icons in the Number cell
    [InvoiceTableFields.frozen]: false,
    [InvoiceTableFields.isLateFee]: false,
    [InvoiceTableFields.hasPaymentPlan]: false,
    [InvoiceTableFields.hasPaymentPromise]: false,
    [InvoiceTableFields.hasOpenQuery]: false,
    [InvoiceTableFields.contactName]: context === `default`,
    [InvoiceTableFields.groupId]: context === `default`,
  }), [context]);
  const savedFilterId = useApplySavedFilterFromUrl(apiRef, SavedFilterType.Invoice, {
    enabled: !showKanbanBoard,
    defaultColumnVisibilityModel,
  });

  useEffect(() => {
    if (context === `contact`) {
      setContactInvoiceListView(`table`);
    }
  }, [context, contactId]);

  // While a search/filter change is in flight RTK Query keeps serving the
  // previous args' `data`, so the grid would keep showing rows for a search the
  // user has already typed past (type 6951, see results for 695). `currentData`
  // is only set for the current args, so changed args blank the rows behind the
  // loading overlay while the 15s poll (same args) leaves them alone.
  const invoices = (isFetching && !currentInvoicesRes ? [] : invoicesRes?.invoices) || [];
  const count = invoicesRes?.count ? Number(invoicesRes.count) : 0;

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: `include`,
    ids: new Set(),
  });

  const tableModels = useInvoiceTableModels();
  const { filterModel, setFilterModel, setPaginationModel, setSortModel, sortModel, paginationModel, resetModels } = tableModels;
  const restoredModels = usePersistedTableModels(`invoice`, tableModels);
  const savedFilterExportParams = toSavedFilterExportParams(buildInvoiceExportParams(filterModel, sortModel, contactId));

  // On first mount, reset the filters, unless we're restoring a saved filter
  useEffect(() => {
    if (urlParams.has(`persist`) || savedFilterId || restoredModels) {
      // Do nothing
    }
    else {
      resetModels();
    }
  }, []);

  const groupOptions = useMemo(() => {
    if (!groupsRes) {
      return [];
    }

    return groupsRes.map(group => {
      return {
        label: group.title,
        value: group.id,
      };
    });
  }, [groupsRes]);

  const onRemoveFilter = (filterId: GridFilterItem[`id`]) => {
    setFilterModel({
      ...filterModel,
      items: filterModel.items.filter(item => item.id !== filterId),
    });
  };

  const onBucketHeadingClick = (bucket: InvoiceAgingBucket) => {
    // Bucket filters replace any active saved filter, so drop its URL marker
    // while keeping any other query params (this also puts the columns back
    // to the default spec)
    if (urlParams.has(`savedFilterId`)) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete(`savedFilterId`);

      const query = searchParams.toString();
      navigate(`${location.pathname}${query ? `?${query}` : ``}`, { replace: true });
    }

    setFilterModel({
      ...filterModel,
      items: [
        ...filterModel.items.filter(item => ![
          InvoiceTableFields.daysOverdue,
          InvoiceTableFields.invoiceStatus,
        ].includes(item.field as InvoiceTableFields)),
        ...buildInvoiceAgingBucketFilterItems(bucket),
      ],
    });

    setInvoiceListView(`table`);
  };

  const columns: GridColDef<InvoiceListItem>[] = useMemo(() => {
    const defaultWidths = {
      [InvoiceTableFields.contactName]: 170,
      [InvoiceTableFields.invoiceReference]: 100,
      [InvoiceTableFields.groupId]: 100,
      [InvoiceTableFields.primaryContactName]: 100,
    };
    const result: GridColDef<InvoiceListItem>[] = [
      {
        field: InvoiceTableFields.frozen,
        type: `singleSelect`,
        valueGetter: (value, row) => row.invoice.frozen || false,
        valueOptions: freezeOptions,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        width: 30,
        headerName: ``,
        renderCell: ({ row }) => {
          if (row.invoice.frozen) {
            return (
              <Tooltip title={ `Invoice is frozen. No policies will apply.` }>
                <span className={ `w-full h-full flex items-center` }>
                  <LockClosedIcon className={ `h-5 w-5 text-blue-400` } />
                </span>
              </Tooltip>
            );
          }

          return (
            <Tooltip title={ `Invoice not frozen` }>
              <span className={ `w-full h-full flex items-center` }>
                <LockOpenIcon className={ `h-5 w-5 text-gray-200` } />
              </span>
            </Tooltip>
          );
        },
        valueFormatter: (value, row) => {
          return row.invoice.frozen ? `Frozen` : ``;
        },
      },
      {
        field: InvoiceTableFields.isLateFee,
        type: `singleSelect`,
        valueGetter: (value, row) => row.invoice.createdByUs || false,
        valueOptions: isLateFeeOptions,
        width: 30,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        headerName: ``,
        renderCell: ({ row }) => {
          if (row.invoice.createdByUs) {
            return (
              <Tooltip title={ `Invoice was created by Paidnice as a late fee or interest charge` }>
                <span className={ `w-full h-full flex items-center` }>
                  <CurrencyDollarIcon className={ `h-5 w-5 text-orange-400` } />
                </span>
              </Tooltip>
            );
          }

          return null;
        },
        valueFormatter: (value, row) => {
          return row.invoice.createdByUs ? `Late Fee Invoice` : ``;
        },
      },
      {
        field: InvoiceTableFields.invoiceSentStatus,
        headerName: `Sent`,
        type: `singleSelect`,
        width: 50,
        hideable: true,
        sortable: false,
        valueGetter: (value, row) => row.invoice.externalSentToCustomerFlag || false,
        valueOptions: [
          { label: `Sent`, value: true },
          { label: `Not Sent`, value: false },
        ],
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        renderCell: ({ row }) => {
          const isSent = row.invoice.externalSentToCustomerFlag || false;

          return (
            <Tooltip title={ isSent ? `Invoice was sent to customer` : `Invoice has not been sent` }>
              <span className={ `w-full h-full flex items-center` }>
                <EnvelopeIcon
                  className={ classNames(
                    `h-5 w-5`,
                    isSent ? `text-green-500` : `text-gray-300`,
                  ) }
                />
              </span>
            </Tooltip>
          );
        },
        valueFormatter: (value, row) => {
          return row.invoice.externalSentToCustomerFlag ? `Sent` : `Not Sent`;
        },
      },
      {
        field: InvoiceTableFields.invoiceNumber,
        headerName: `Number`,
        type: `string`,
        width: 150,
        valueGetter: (value, row) => row.invoice.number,
        filterOperators: getGridStringOperators().filter(op => op.value === `contains`),
        renderCell: ({ row }) => (
          <span className={ `flex min-w-0 items-center gap-1.5` }>
            <WrappedLink
              to={ `/invoices/${ row.invoice.id }` }
            >
              <Paragraph
                variant={ `link` }
                as={ `span` }
              >
                { row.invoice.number || `No Number` }
              </Paragraph>
            </WrappedLink>
            { row.invoice.frozen ? (
              <Tooltip title={ `Invoice is frozen. No policies will apply.` }>
                <span className={ `inline-flex shrink-0 items-center` }>
                  <LockClosedIcon className={ `h-4 w-4 text-blue-400` } />
                </span>
              </Tooltip>
            ) : null }
            { row.invoice.createdByUs ? (
              <Tooltip title={ `Invoice was created by Paidnice as a late fee or interest charge` }>
                <span className={ `inline-flex shrink-0 items-center` }>
                  <CurrencyDollarIcon className={ `h-4 w-4 text-orange-400` } />
                </span>
              </Tooltip>
            ) : null }
            { row.invoice.hasPaymentPlan ? (
              <Tooltip title={ `Invoice has a current payment plan in progress` }>
                <span className={ `inline-flex shrink-0 items-center` }>
                  <CalendarDateRangeIcon className={ `h-4 w-4 text-blue-400` } />
                </span>
              </Tooltip>
            ) : null }
            { row.invoice.hasPaymentPromise ? (
              <Tooltip title={ `Customer has promised to pay this invoice` }>
                <span className={ `inline-flex shrink-0 items-center` }>
                  <HandRaisedIcon className={ `h-4 w-4 text-blue-400` } />
                </span>
              </Tooltip>
            ) : null }
            { row.invoice.hasOpenQuery ? (
              <Tooltip title={ `This invoice has an open query` }>
                <span className={ `inline-flex shrink-0 items-center` }>
                  <ExclamationTriangleIcon className={ `h-4 w-4 text-orange-400` } />
                </span>
              </Tooltip>
            ) : null }
          </span>
        ),
      },
      {
        field: InvoiceTableFields.invoiceReference,
        headerName: `Reference`,
        type: `string`,
        width: defaultWidths[InvoiceTableFields.invoiceReference],
        valueGetter: (value, row) => row.invoice.reference,
        filterOperators: getGridStringOperators().filter(op => op.value === `contains`),
      },
      {
        field: InvoiceTableFields.invoiceStatus,
        headerName: `Status`,
        type: `singleSelect`,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: statusOptions,
        valueGetter: (value, row) => row.invoice.externalStatus,
        renderCell: ({ row }) => (
          <XeroStatusBadge
            status={ row.invoice.externalStatus as keyof typeof statusUIMapXero }
            mode={ `app` }
          />
        ),
      },
      {
        field: InvoiceTableFields.contactName,
        headerName: `Contact`,
        type: `string`,
        width: defaultWidths[InvoiceTableFields.contactName],
        valueGetter: (value, row) => row.contact.name,
        filterOperators: getGridStringOperators().filter(op => op.value === `contains`),
        renderCell: ({ row }) => (
          <WrappedLink
            to={ `/contacts/${ row.invoice.contactId }` }
          >
            <Paragraph
              variant={ `link` }
              as={ `span` }
            >
              { row.contact.name }
            </Paragraph>
          </WrappedLink>
        ),
      },
      {
        field: InvoiceTableFields.primaryContactName,
        headerName: `Primary Contact`,
        type: `string`,
        width: defaultWidths[InvoiceTableFields.primaryContactName],
        valueGetter: (value, row) => row.contact.primaryContactName,
        filterOperators: getGridStringOperators().filter(op => op.value === `contains`),
        renderCell: ({ row }) => (
          <WrappedLink
            to={ `/contacts/${ row.invoice.contactId }` }
          >
            <Paragraph
              variant={ `link` }
              as={ `span` }
            >
              { row.contact.primaryContactName }
            </Paragraph>
          </WrappedLink>
        ),
      },
      {
        field: InvoiceTableFields.invoiceIssueDate,
        headerName: `Issue Date`,
        valueGetter: (value, row) => row.invoice.issueDate,
        filterOperators: getGridDateOperators()
          .filter(op => {
            return [ `onOrAfter`, `onOrBefore`].includes(op.value);
          })
          .map(op => {
            return {
              ...op,
              InputComponent: CustomDateFilterInput,
            };
          }),
        width: 110,
      },
      {
        field: InvoiceTableFields.invoiceDueDate,
        headerName: `Due Date`,
        valueGetter: (value, row) => row.invoice.dueDate,
        filterOperators: getGridDateOperators()
          .filter(op => {
            return [ `onOrAfter`, `onOrBefore`].includes(op.value);
          })
          .map(op => {
            return {
              ...op,
              InputComponent: CustomDateFilterInput,
            };
          }),
        width: 110,
      },
      {
        field: InvoiceTableFields.expectedPaymentDate,
        headerName: `Expected Payment Date`,
        valueGetter: (value, row) => row.invoice.expectedPaymentDate,
        filterOperators: getGridDateOperators()
          .filter(op => {
            return [ `onOrAfter`, `onOrBefore`].includes(op.value);
          })
          .map(op => {
            return {
              ...op,
              InputComponent: CustomDateFilterInput,
            };
          }),
        width: 110,
      },
      {
        field: InvoiceTableFields.amountLastChangedDate,
        headerName: `Amount Updated`,
        valueGetter: (value, row) => row.invoice.amountLastChangedDate,
        // Not backed by a sortable/filterable DB column on the list endpoint
        sortable: false,
        filterable: false,
        width: 110,
      },
      {
        field: InvoiceTableFields.invoiceTotal,
        type: `number`,
        headerName: `Total`,
        valueGetter: (value, row) => toDecimal(row.invoice.totals.totalInCents),
        valueFormatter: (value, row) => {
          const currency = row.invoice.currency;

          return currencyFormatter(currency)(value);
        },
        filterOperators: getGridNumericOperators().filter(op => {
          return [`>=`, `=`, `<=`].includes(op.value);
        }),
      },
      {
        field: InvoiceTableFields.invoiceOutstanding,
        type: `number`,
        headerName: `Outstanding`,
        valueGetter: (value, row) => toDecimal(row.invoice.totals.dueInCents),
        valueFormatter: (value, row) => {
          const currency = row.invoice.currency;

          return currencyFormatter(currency)(value);
        },
        filterOperators: getGridNumericOperators().filter(op => {
          return [`>=`, `=`, `<=`].includes(op.value);
        }),
        renderCell: ({ row, formattedValue, value }) => {
          if (value === null || value === undefined) {
            return <span className={ `text-gray-300` }>{ `—` }</span>;
          }

          if (!value) {
            // Zero outstanding: shown, but de-emphasised — never the alarm red
            return <span className={ `text-gray-400` }>{ formattedValue }</span>;
          }

          const isOverdue = row.invoice.externalStatus === `AUTHORISED` && row.invoice.daysOverdue >= 1;

          // The outstanding amount is now an overdue amount
          return (
            <span className={ isOverdue ? `font-semibold text-red-500` : `` }>
              { formattedValue }
            </span>
          );
        },
      },
      {
        field: InvoiceTableFields.daysOverdue,
        headerName: `Days Overdue`,
        type: `number`,
        width: 80,
        valueGetter: (value, row) => row.invoice.externalStatus === `AUTHORISED` ? row.invoice.daysOverdue : null,
        filterOperators: getGridNumericOperators().filter(op => {
          return [`>=`, `=`, `<=`].includes(op.value);
        }),
        renderCell: ({ value }) => {
          if (value === null || value === undefined) {
            return null;
          }

          if (value < 1) {
            return <span className={ `text-gray-300` }>{ `—` }</span>;
          }

          const pillClasses = value > 28
            ? `bg-red-100 text-red-700`
            : value > 14
              ? `bg-orange-100 text-orange-700`
              : `bg-amber-100 text-amber-700`;

          return (
            <span className={ `inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${ pillClasses }` }>
              { `${ value }d` }
            </span>
          );
        },
      },
      {
        field: InvoiceTableFields.groupId,
        headerName: `Group`,
        description: `Paidnice Group`,
        type: `singleSelect`,
        width: defaultWidths[InvoiceTableFields.groupId],
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: groupOptions,
        valueGetter: (value, row) => row.group?.title,
        renderCell: ({ row }) => (
          <WrappedLink
            to={ `/groups/${ row.group?.id }` }
          >
            <Paragraph
              variant={ `link` }
              as={ `span` }
            >
              { row.group?.title }
            </Paragraph>
          </WrappedLink>
        ),
      },
      {
        field: InvoiceTableFields.invoiceTag,
        headerName: `Tags`,
        type: `singleSelect`,
        width: 84,
        sortable: false,
        valueOptions: invoiceTagsList.map(tag => ({ label: tag, value: tag })),
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueGetter: (value, row) => row.invoice.tags?.join(`, `) || ``,
        renderCell: ({ row }) => {
          if (!row.invoice.tags?.length) {
            return null;
          }

          return (
            <span className={ `flex h-full items-center gap-1 overflow-hidden` }>
              { row.invoice.tags.map(tag => (
                <TagBadge
                  key={ tag }
                  name={ tag }
                  color={ invoiceTagColorMap[tag] }
                />
              )) }
            </span>
          );
        },
      },
      {
        field: InvoiceTableFields.currency,
        headerName: `Currency`,
        width: 60,
        valueGetter: (value, row) => row.invoice.currency,
        type: `string`,
      },
      {
        field: InvoiceTableFields.hasPaymentPlan,
        type: `singleSelect`,
        valueGetter: (value, row) => row.invoice.hasPaymentPlan || false,
        valueOptions: hasPaymentPlanOptions,
        width: 30,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        headerName: ``,
        renderCell: ({ row }) => {
          if (row.invoice.hasPaymentPlan) {
            return (
              <Tooltip title={ `Invoice has a current payment plan in progress` }>
                <span className={ `w-full h-full flex items-center` }>
                  <CalendarDateRangeIcon className={ `h-5 w-5 text-blue-400` } />
                </span>
              </Tooltip>
            );
          }

          return null;
        },
        valueFormatter: (value, row) => {
          return row.invoice.hasPaymentPlan ? `Yes` : `No`;
        },
        sortable: false,
      },
      {
        field: InvoiceTableFields.hasPaymentPromise,
        type: `singleSelect`,
        valueGetter: (value, row) => row.invoice.hasPaymentPromise || false,
        valueOptions: hasPaymentPromiseOptions,
        width: 30,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        headerName: ``,
        renderCell: ({ row }) => {
          if (row.invoice.hasPaymentPromise) {
            return (
              <Tooltip title={ `Customer has promised to pay this invoice` }>
                <span className={ `w-full h-full flex items-center` }>
                  <HandRaisedIcon className={ `h-5 w-5 text-blue-400` } />
                </span>
              </Tooltip>
            );
          }

          return null;
        },
        valueFormatter: (value, row) => {
          return row.invoice.hasPaymentPromise ? `Yes` : `No`;
        },
        sortable: false,
      },
      {
        field: InvoiceTableFields.hasOpenQuery,
        type: `singleSelect`,
        valueGetter: (value, row) => row.invoice.hasOpenQuery || false,
        valueOptions: hasOpenQueryOptions,
        width: 30,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        headerName: `Query`,
        renderCell: ({ row }) => {
          if (row.invoice.hasOpenQuery) {
            return (
              <Tooltip title={ `This invoice has an open query` }>
                <span className={ `w-full h-full flex items-center` }>
                  <ExclamationTriangleIcon className={ `h-5 w-5 text-orange-400` } />
                </span>
              </Tooltip>
            );
          }

          return null;
        },
        valueFormatter: (value, row) => {
          return row.invoice.hasOpenQuery ? `Yes` : `No`;
        },
        sortable: false,
      },
      {
        field: `notes`,
        headerName: `Notes`,
        width: 70,
        sortable: false,
        filterable: false,
        disableExport: true,
        align: `center`,
        headerAlign: `center`,
        renderCell: ({ row }) => (
          <NotesCellButton
            resource={ { type: `invoice`, id: row.invoice.id } }
            latestNote={ row.latestNote }
          />
        ),
      },
      {
        field: `actions`,
        type: `actions`,
        width: 28,
        getActions: ({ row }) => {
          const opts = [
            <GridActionsCellItem
              onClick={ () => navigate(`${row.invoice.id}/policy-preview`) }
              label={ `Preview Policies` }
              showInMenu
              key={ `${row.invoice.id}-preview` }
            />,
            <GridActionsCellItem
              onClick={ () => handleCustomerPortal(row.invoice.hash) }
              label={ `View Customer Portal` }
              showInMenu
              key={ `${row.invoice.id}-portal` }
            />,
          ];

          const externalLink = getAccountingSystemDeepLink(currentOrg, `invoice`, row.invoice.externalId);
          if (externalLink) {
            opts.unshift(
              <GridActionsCellItem
                onClick={ () => window.open(externalLink, `_blank`) }
                label={ `View In ${accountingSystemName}` }
                showInMenu
                key={ `${row.invoice.id}-external` }
              />,
            );
          }

          if (row.invoice.frozen) {
            opts.push(
              <GridActionsCellItem
                onClick={ () => {
                  actionInvoice({
                    invoiceId: row.invoice.id,
                    action: `reset`,
                    organisationId: currentOrg.id,
                  });
                } }
                label={ `Unfreeze Invoice` }
                showInMenu
                key={ `${row.invoice.id}-resume` }
              />,
            );
          }
          else {
            opts.push(
              <GridActionsCellItem
                onClick={ () => {
                  actionInvoice({
                    invoiceId: row.invoice.id,
                    action: `freeze`,
                    organisationId: currentOrg.id,
                  });
                } }
                label={ `Freeze Invoice` }
                showInMenu
                key={ `${row.invoice.id}-freeze` }
              />,
            );
          }

          return opts;
        },
      },
    ];

    // Wrap the stacked columns so their cell renders the secondary column's
    // value as a smaller second line (see stackedColumns config above)
    return result.map(col => {
      const stacked = stackedColumns.find(entry => entry.field === col.field);

      if (!stacked) {
        return col;
      }

      const renderPrimary = col.renderCell;

      return {
        ...col,
        renderCell: (params: GridRenderCellParams<InvoiceListItem>) => {
          const secondaryValue = stacked.getSecondaryValue(params.row);

          return (
            <span className={ `flex h-full min-w-0 flex-col justify-center leading-tight` }>
              <span className={ `flex min-w-0 items-center` }>
                { renderPrimary ? renderPrimary(params) : String(params.formattedValue ?? ``) }
              </span>
              { secondaryValue ? (
                <span className={ `truncate text-xs text-gray-400` }>{ secondaryValue }</span>
              ) : null }
            </span>
          );
        },
      };
    });
  }, [invoiceTagsList, invoiceTagColorMap]);

  function handleCustomerPortal(hash: string) {
    const l = `${env.portalWebUrl}/i/${hash}`;

    window.open(l, `_blank`);

    return;
  }

  function getRowClassName(params: GridRowClassNameParams<InvoiceListItem>){
    const classname = classNames(
      params.row.invoice.frozen ? `bg-sky-50` : ``,
    );

    return classname;
  }

  function onSync() {
    actionInvoices({ action: `sync-all`, organisationId: currentOrg.id });
  }

  const headerSubtitle = showKanbanBoard
    ? `Unpaid invoices grouped by days overdue`
    : `Invoices will be automatically imported as they are created in ${accountingSystemName} in real time.`;

  const viewportHeight = window.innerHeight;
  const tableHeight = viewportHeight
    - 160; // px for the header

  return (
    <div
      style={ context === `default` || !showKanbanBoard ? { height: tableHeight } : undefined }
      className={ `flex flex-col` }
    >
      { context === `default` ? (
        <div className={ `mb-3` }>
          <div className={ `flex items-start justify-between gap-4` }>
            <div className={ `min-w-0` }>
              <Heading
                variant={ `default` }
                size={ `xl` }
              >
                { `Invoices` }
              </Heading>
              <Paragraph
                variant={ `help` }
                className={ `mt-0.5` }
              >
                { headerSubtitle }
              </Paragraph>
            </div>
            <Tooltip title={ `Force a re-sync of invoices now.` }>
              <span className={ `shrink-0` }>
                <Button
                  onClick={ onSync }
                  disabled={ currentOrg?.syncInProgress }
                  color={ `paidniceGreenOutline` }
                  size={ `sm` }
                >
                  { currentOrg?.syncInProgress ? `Checking for updates...` : `Check for updates` }
                </Button>
              </span>
            </Tooltip>
          </div>
          <div className={ `mt-3` }>
            <InvoiceViewToggle
              value={ invoiceListView }
              onChange={ setInvoiceListView }
            />
          </div>
        </div>
      ) : (
        <div className={ `mb-3` }>
          <InvoiceViewToggle
            value={ invoiceListView }
            onChange={ setInvoiceListView }
          />
        </div>
      ) }

      { showKanbanBoard ? (
        <div className={ classNames(
          `min-h-0 overflow-hidden`,
          context === `contact` ? `` : `flex-1`,
        ) }>
          <InvoiceKanbanBoard
            fitContent={ context === `contact` }
            onBucketHeadingClick={ onBucketHeadingClick }
          />
        </div>
      ) : (
        <DatagridCard className={ `flex-1` }>
          <DataGridPro
            apiRef={ apiRef }
            className={ `bg-white` }
            rows={ invoices }
            columns={ columns }
            loading={ isLoading || isFetching }
            filterMode={ `server` }
            paginationMode={ `server` }
            sortingMode={ `server` }
            showToolbar

            // Filter Opts
            filterDebounceMs={ 300 }
            onFilterModelChange={ setFilterModel }
            filterModel={ filterModel }

            // Enable pagination
            pagination
            pageSizeOptions={ [10, 25, 50, 100] }
            onPaginationModelChange={ setPaginationModel }
            rowCount={ count }
            paginationModel={ paginationModel }

            // Sorting
            sortModel={ sortModel }
            onSortModelChange={ setSortModel }

            // Checkboxes
            checkboxSelection
            rowSelectionModel={ rowSelectionModel }
            onRowSelectionModelChange={ model => onRowSelectionChangeWrapper(model, invoices.map(i => i.id), setRowSelectionModel) }
            keepNonExistentRowsSelected

            // Styling
            getRowClassName={ getRowClassName }
            rowHeight={ 52 }
            disableColumnMenu
            columnHeaderHeight={ 38 }

            disableVirtualization

            disableColumnPinning
            initialState={ {
              pinnedColumns: {
                left: [GRID_CHECKBOX_SELECTION_FIELD],
              },
              columns: {
                columnVisibilityModel: defaultColumnVisibilityModel,
              },
            } }

            // Slots & Customisation
            slots={ {
              toolbar: CustomToolbar,
              filterPanel: ApiSavedFilterPanel,
            } }
            slotProps={ {
              columnsManagement: {
                getTogglableColumns: cols => getTogglableColumns(cols, [`actions`]),
              },
              filterPanel: {
                savedFilterType: SavedFilterType.Invoice,
                exportParams: savedFilterExportParams,
                logicOperators: [GridLogicOperator.And],
                getColumnForNewFilter: getColumnForNewFilter,
                filterFormProps: {
                  filterColumns: args => filterColumns(args, context === `contact` ? [
                    InvoiceTableFields.contactName,
                    InvoiceTableFields.primaryContactName,
                    InvoiceTableFields.groupId,
                  ]: [], true),
                },
              },
              toolbar: {
                showQuickFilter: true,
                rowSelectionModel,
                onRemoveFilter,
                context,
              },
              pagination: {
                showFirstButton: true,
                showLastButton: true,
              },
            } }
            sx={ {
              ...commonConfig.headerStyle,
              ...commonConfig.toolbarStyle,
              '--DataGrid-overlayHeight': `300px`,
            } }
            localeText={ {
              toolbarColumns: `Hide Columns`,
            } }
          />
        </DatagridCard>
      ) }
    </div>
  );
}

function CustomToolbar({ rowSelectionModel, onRemoveFilter, context }: ToolbarProps) {
  const currentOrg = useGetSelectedOrganisation();
  const { filterModel, sortModel } = useInvoiceTableModels();
  const { contactId: contactIdParam } = useParams();
  const [exportAllInvoices] = useExportAllInvoicesMutation();

  async function onExportAll(filename: string | undefined, columns: string[]) {
    const params = buildInvoiceExportParams(filterModel, sortModel, contactIdParam);

    try {
      const response = await exportAllInvoices({
        ...params,
        organisationId: currentOrg?.id,
        filename,
        columns,
      }).unwrap();

      toast.success(`Export queued. The CSV will be emailed to ${response.email} shortly.`);
    }
    catch (e) {
      console.error(e);
      toast.error(`Could not queue the export. Please try again.`);
    }
  }

  // If context is contact, then render the actions on new line
  const presetFilters = context === `contact` ? (
    <InvoiceBulkActionsToolbar
      rowSelectionModel={ rowSelectionModel }

    />
  ) : null;

  const children = context !== `contact` ? (
    <InvoiceBulkActionsToolbar
      rowSelectionModel={ rowSelectionModel }
    />
  ) : null;

  return (
    <CustomGenericToolbar
      rowSelectionModel={ rowSelectionModel }
      onRemoveFilter={ onRemoveFilter }
      onExportAll={ onExportAll }
      context={ `invoices` }
      presetFilters= { presetFilters }
      leftActions={ <SaveFilterButton type={ SavedFilterType.Invoice }
        exportParams={ toSavedFilterExportParams(buildInvoiceExportParams(filterModel, sortModel, contactIdParam)) } /> }
    >
      { children }
    </CustomGenericToolbar>
  );
}

// Custom component that renders the date filter input without a placeholder
function CustomDateFilterInput(props) {
  return (
    <GridFilterInputDate
      { ...props }
      label={ props.value ? props.label : ` ` } // Workaround for placeholder always showing
    />
  );
}
